<template>
  <ui-sheet v-model="model" footer-class="rm-period-footer" :title="$t('report_mobile.period')">
    <div class="rm-period-presets">
      <button
        v-for="preset in presets"
        :key="preset.key"
        type="button"
        class="rm-period-preset"
        :class="{ 'is-active': draftPresetKey === preset.key }"
        :aria-pressed="String(draftPresetKey === preset.key)"
        @click="draft = [preset.from, preset.to]"
      >
        {{ $t(`report_mobile.presets.${preset.key}`) }}
      </button>
    </div>

    <report-calendar
      :start="draft[0] || ''"
      :end="draft[1] || ''"
      @pick="pickDay"
    ></report-calendar>
    <template #footer>
      <div class="rm-period-footer__text">
        <span class="rm-period-footer__range rm-num">{{ rangeText }}</span>
        <span class="rm-muted">{{ rangeHint }}</span>
      </div>
      <button
        type="button"
        class="rm-btn rm-btn--primary rm-period-footer__apply"
        @click="apply"
      >
        {{ $t("report_mobile.apply") }}
      </button>
    </template>
  </ui-sheet>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPlural from "../shared/hasPlural";
import ReportCalendar from "../shared/ReportCalendar.vue";
import { countDays, findPresetKey, formatRange, normalizeRange } from "../shared/periods";

export default {
  name: "ReportMobilePeriodSheet",
  mixins: [hasModel, hasPlural],
  components: { ReportCalendar },
  props: {
    from: {
      type: String,
      required: true,
    },
    to: {
      type: String,
      required: true,
    },
    presets: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    draft: [],
  }),
  computed: {
    isPickingEnd() {
      return this.draft.length === 1;
    },
    draftRange() {
      return normalizeRange(this.draft);
    },
    draftPresetKey() {
      if (this.isPickingEnd) {
        return null;
      }
      return findPresetKey(this.presets, ...this.draftRange);
    },
    rangeText() {
      if (this.isPickingEnd) {
        return `${formatRange(this.draft[0])} – …`;
      }
      return formatRange(...this.draftRange);
    },
    rangeHint() {
      if (this.isPickingEnd) {
        return this.$t("report_mobile.pick_end_date");
      }
      return this.pluralT("report_mobile.days_n", countDays(...this.draftRange));
    },
  },
  watch: {
    value: {
      handler(isOpen) {
        if (isOpen) {
          this.draft = [this.from, this.to];
        }
      },
      immediate: true,
    },
  },
  methods: {
    /** Prvi dodir bira početak, drugi kraj; raniji datum od početka ili završen opseg počinje iznova. */
    pickDay(date) {
      if (this.draft.length !== 1 || date < this.draft[0]) {
        this.draft = [date];
      } else {
        this.draft = [this.draft[0], date];
      }
    },
    apply() {
      const [from, to] = this.draftRange;
      this.$emit("apply", { from, to });
      this.model = false;
    },
  },
};
</script>

<style lang="scss">
.rm-period-presets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.rm-period-preset {
  height: 44px;
  padding: 0 6px;
  border-radius: 12px;
  border: 1px solid var(--rm-border-strong);
  background: var(--rm-surface);
  color: var(--rm-text);
  font-size: 14px;
  font-weight: 500;

  &.is-active {
    border: 2px solid var(--rm-accent);
    background: var(--rm-accent-soft);
    color: var(--rm-accent-strong);
    font-weight: 700;
  }
}

.rm-period-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  &__text {
    display: flex;
    flex-direction: column;
  }

  &__range {
    font-size: 16px;
    font-weight: 700;
  }

}

.rm-theme .rm-period-footer__apply.rm-btn {
  width: auto;
  padding: 0 24px;
}
</style>
